import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Icon, Swatch, type SwatchKind } from "@/components/primitives";
import { useNotifications, type NotificationItem, type NotificationKind } from "@/hooks/useNotifications";

export default function Notifications() {
  const nav = useNavigate();
  const { items, unseenCount, markAllSeen } = useNotifications();

  return (
    <MobileFrame>
      <div style={{ padding: "20px 20px 12px", display: "flex", alignItems: "center", gap: 10 }}>
        <button
          onClick={() => nav(-1)}
          aria-label="Back"
          style={{
            background: "none", border: "1px solid hsl(var(--ink))",
            padding: 6, cursor: "pointer", color: "hsl(var(--ink))",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 32, height: 32,
          }}
        >
          {Icon.chev(14)}
        </button>
        <h1 style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontWeight: 800, fontSize: 22, color: "hsl(var(--ink))", margin: 0, lineHeight: 1,
        }}>
          Notifications
        </h1>
        <button
          onClick={() => void markAllSeen()}
          disabled={unseenCount === 0}
          style={{
            marginLeft: "auto", background: "none", border: "1px solid hsl(var(--ink))",
            padding: "6px 10px", cursor: unseenCount === 0 ? "default" : "pointer",
            color: "hsl(var(--ink))", fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
            opacity: unseenCount === 0 ? 0.4 : 1,
          }}
        >
          Mark all read
        </button>
      </div>

      <div style={{ borderTop: "1px solid hsl(var(--ink))" }}>
        {items.length === 0 ? (
          <div style={{
            padding: "40px 20px", textAlign: "center",
            color: "hsl(var(--ink-2))", fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
          }}>
            You're all caught up.
          </div>
        ) : (
          items.map((n) => (
            <NotificationRow key={n.id} item={n} onClick={() => nav(n.href)} />
          ))
        )}
      </div>
    </MobileFrame>
  );
}

function tagFor(kind: NotificationKind): { label: string; bg: string; fg: string } {
  // Each badge pairs its bg with the matching auto-derived `-fg` so the
  // label is always readable, even on themes where the bg flips light/dark
  // (Velvet Underground's "cream" is dark twilight; its "marigold" is light
  // lavender). For alpha-overlay backgrounds (rust at 15%/20%) we fall back
  // to the surrounding ink-fg, which auto-derives to whatever neutral is
  // safe on the page background.
  switch (kind) {
    case "new_follower":          return { label: "Follow",    bg: "hsl(var(--marigold))",     fg: "hsl(var(--marigold-fg))" };
    case "new_note":              return { label: "Note",      bg: "hsl(var(--cream))",        fg: "hsl(var(--cream-fg))" };
    case "new_love":              return { label: "Love",      bg: "hsl(var(--rust) / 0.15)",  fg: "hsl(var(--ink))" };
    case "grail_match":           return { label: "Grail ✦",  bg: "hsl(var(--marigold))",     fg: "hsl(var(--marigold-fg))" };
    case "new_message":           return { label: "DM",        bg: "hsl(var(--cream))",        fg: "hsl(var(--cream-fg))" };
    case "verification_complete": return { label: "Verified",  bg: "hsl(var(--marigold))",     fg: "hsl(var(--marigold-fg))" };
    case "verified_approved":     return { label: "Verified ✦",bg: "hsl(var(--marigold))",     fg: "hsl(var(--marigold-fg))" };
    case "post_removed":          return { label: "Removed",   bg: "hsl(var(--rust) / 0.2)",   fg: "hsl(var(--ink))" };
    case "post_reinstated":       return { label: "Restored",  bg: "hsl(var(--cream))",        fg: "hsl(var(--cream-fg))" };
    default:                      return { label: "Notice",    bg: "hsl(var(--cream))",        fg: "hsl(var(--cream-fg))" };
  }
}

function NotificationRow({ item, onClick }: { item: NotificationItem; onClick: () => void }) {
  const tag = tagFor(item.kind);
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", gap: 12, alignItems: "flex-start",
        width: "100%", textAlign: "left", padding: "14px 20px",
        background: !item.read ? "hsl(var(--cream) / 0.6)" : "transparent",
        border: "none", borderBottom: "1px solid hsl(var(--ink) / 0.12)",
        cursor: "pointer", color: "hsl(var(--ink))",
      }}
    >
      <Swatch
        kind={(item.actorAvatar ?? "cream") as SwatchKind}
        style={{ width: 36, height: 36, border: "1px solid hsl(var(--ink))", flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{
            display: "inline-block", padding: "1px 6px",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
            letterSpacing: "0.1em", textTransform: "uppercase",
            border: "1px solid hsl(var(--ink))", background: tag.bg, color: tag.fg,
          }}>
            {tag.label}
          </span>
          {!item.read && (
            <span aria-label="Unread" style={{ width: 6, height: 6, borderRadius: "50%", background: "hsl(var(--rust))" }} />
          )}
        </div>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
          {item.title}
        </div>
        {item.preview && (
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "hsl(var(--ink-2))", lineHeight: 1.3, marginTop: 2,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {item.preview}
          </div>
        )}
      </div>
    </button>
  );
}
